import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../../firebase';
import CategoryEditor from './CategoryEditor';
import ItemEditor from './ItemEditor';

const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);
  const [showItemEditor, setShowItemEditor] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'categories'));
      const categoriesData = querySnapshot.docs.map((doc) => ({
        firestoreId: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setEditMode(false);
    setShowCategoryEditor(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setEditMode(true);
    setShowCategoryEditor(true);
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`Delete category "${category.title}"? This will remove all items in it.`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'categories', category.firestoreId));
      await fetchCategories();
      toast.success('Category deleted successfully');
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      if (editMode && selectedCategory) {
        await updateDoc(doc(db, 'categories', selectedCategory.firestoreId), categoryData);
      } else {
        await addDoc(collection(db, 'categories'), categoryData);
      }
      await fetchCategories();
      setShowCategoryEditor(false);
      toast.success(`Category ${editMode ? 'updated' : 'added'} successfully`);
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };

  const handleAddItem = (category) => {
    setSelectedCategory(category);
    setSelectedItem(null);
    setEditMode(false);
    setShowItemEditor(true);
  };

  const handleEditItem = (category, item) => {
    setSelectedCategory(category);
    setSelectedItem(item);
    setEditMode(true);
    setShowItemEditor(true);
  };

  const handleDeleteItem = async (category, itemId) => {
    if (!window.confirm('Delete this item?')) {
      return;
    }

    try {
      const updatedItems = category.items.filter((item) => item.id !== itemId);
      await updateDoc(doc(db, 'categories', category.firestoreId), {
        items: updatedItems,
      });
      await fetchCategories();
      toast.success('Item deleted successfully');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleSaveItem = async (itemData) => {
    try {
      const category = categories.find((c) => c.firestoreId === selectedCategory.firestoreId);
      let updatedItems;

      if (editMode && selectedItem) {
        updatedItems = category.items.map((item) =>
          item.id === selectedItem.id ? itemData : item
        );
      } else {
        updatedItems = [...(category.items || []), itemData];
      }

      await updateDoc(doc(db, 'categories', selectedCategory.firestoreId), {
        items: updatedItems,
      });

      await fetchCategories();
      setShowItemEditor(false);
      toast.success(`Item ${editMode ? 'updated' : 'added'} successfully`);
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Failed to save item');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading categories...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-medium text-gray-900">Categories Management</h2>
        <button
          onClick={handleAddCategory}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          + Add Category
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No categories found. Add your first category!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map((category) => (
            <div key={category.firestoreId} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium text-gray-900">{category.title}</h3>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        category.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {category.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">{category.subtitle}</p>
                  <p className="text-gray-500 text-xs mt-1">ID: {category.id}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditCategory(category)}
                    className="px-3 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category)}
                    className="px-3 py-1 bg-red-50 hover:bg-red-100 text-red-600 rounded text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Items */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Items ({category.items?.length || 0})
                  </h4>
                  <button
                    onClick={() => handleAddItem(category)}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm"
                  >
                    + Add Item
                  </button>
                </div>

                {category.items && category.items.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {category.items.map((item) => (
                      <div
                        key={item.id}
                        className="border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-start gap-3">
                          {item.images && item.images[0] && (
                            <img
                              src={item.images[0]}
                              alt={item.title}
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <h5 className="font-medium text-sm text-gray-900 truncate">
                              {item.title}
                            </h5>
                            <p className="text-xs text-gray-500">MOQ: {item.moq}</p>
                            <p className="text-xs text-gray-500">{item.estPrice}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleEditItem(category, item)}
                            className="flex-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded text-xs"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteItem(category, item.id)}
                            className="flex-1 px-2 py-1 bg-red-50 hover:bg-red-100 text-red-600 rounded text-xs"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No items in this category</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Category Editor Modal */}
      {showCategoryEditor && (
        <CategoryEditor
          category={selectedCategory}
          onSave={handleSaveCategory}
          onClose={() => setShowCategoryEditor(false)}
        />
      )}

      {/* Item Editor Modal */}
      {showItemEditor && (
        <ItemEditor
          item={selectedItem}
          onSave={handleSaveItem}
          onClose={() => setShowItemEditor(false)}
        />
      )}
    </div>
  );
};

export default CategoriesManager;
